import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Shield, Lock, Eye, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Navigation from "@/components/sections/Navigation";
import Footer from "@/components/sections/Footer";

const PrivacyPolicy = () => {
  const { t } = useLanguage();
  useScrollReveal();

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      {/* Hero Section */}
      <section className="py-32 bg-gradient-to-br from-primary/10 via-background to-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center scroll-reveal">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="p-4 rounded-2xl bg-primary/10">
                <Shield className="h-10 w-10 text-primary" />
              </div>
              <h1 className="text-4xl md:text-6xl font-black bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                {t("privacy.title")}
              </h1>
            </div>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {t("privacy.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Information Collection */}
            <Card className="scroll-reveal bg-background/60 border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <Eye className="h-6 w-6 text-primary" />
                  {t("privacy.collection.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t("privacy.collection.description")}
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  {t("privacy.collection.items").map((item: string, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Data Usage */}
            <Card className="scroll-reveal bg-background/60 border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <FileText className="h-6 w-6 text-primary" />
                  {t("privacy.usage.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t("privacy.usage.description")}
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  {t("privacy.usage.items").map((item: string, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Data Protection */}
            <Card className="scroll-reveal bg-background/60 border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <Lock className="h-6 w-6 text-primary" />
                  {t("privacy.protection.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t("privacy.protection.description")}
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  {t("privacy.protection.items").map((item: string, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="scroll-reveal bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20 shadow-lg">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  {t("privacy.contact.title")}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t("privacy.contact.description")}
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;